/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IFooterCategoryViewProps } from '@msdyn365-commerce-modules/footer';
import { addThrottledEvent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { ReactNode, useEffect, useState } from 'react';

//  VSI Customization - START
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import AccordionItem from 'mfcl/AccordionItem';
import { IFooterCategoryProps } from '../definition-extensions/footer-category.ext.props.autogenerated';
// VSI Customization - END

export const FooterCategoryView: React.FC<IFooterCategoryViewProps & IFooterCategoryProps<{}>> = props => {
    const { categories, context, FooterList, FooterListItem } = props;
    if (!categories || !categories.length) {
        props.context.telemetry.warning('Footer content is empty, module wont render');
        return null;
    }

    //  VSI Customization - START

    // isMobile state used to check viewport for accordion view
    const [isMobile, setIsMobile] = useState(props.context.request.device.Type === 'Mobile');
    useEffect(() => {
        MsDyn365.isBrowser && addThrottledEvent(window, 'resize', () => updateViewPort(context, setIsMobile));
        updateViewPort(context, setIsMobile);
    });

    const accordionItemHeading = categories[0] && (categories[0] as JSX.Element).props.config.heading?.text;
    const enableAccordion = props.config.enableAccordion;

    return (
        <Module {...FooterList}>
            {!isMobile || !enableAccordion ? (
                categories.map((category: ReactNode, index: number) => {
                    return (
                        <Node {...FooterListItem} key={index}>
                            {category}
                        </Node>
                    );
                })
            ) : (
                <AccordionItem
                    title={accordionItemHeading}
                    id={accordionItemHeading}
                    content={categories.map((category: ReactNode, index: number) => {
                        // first footer-item is heading
                        if (index === 0) {
                            return;
                        }
                        return (
                            <Node {...FooterListItem} key={index}>
                                {category}
                            </Node>
                        );
                    })}
                    // need to set this to prevent accordion from getting focused on load
                    focused={0}
                />
            )}
        </Module>
    );
};

// update isMobile state when window is resized
const updateViewPort = (context: ICoreContext, updateState: React.Dispatch<React.SetStateAction<boolean>>): void => {
    let viewport = 'xs';

    if (MsDyn365.isBrowser && window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                viewport = 'xs';
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                viewport = 'sm';
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                viewport = 'md';
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                viewport = 'lg';
            } else {
                viewport = 'xl';
            }
        }
    }

    viewport === 'xs' || viewport === 'sm' ? updateState(true) : updateState(false);
};

//  VSI Customization - END

export default FooterCategoryView;
